import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { format } from 'date-fns'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { NoData } from 'components/NoData'
import { getFuelDispensers } from 'features/fuelManagement/fuelCustodySlice'
import { Flex } from 'styling/GlobalStyles'
import { tokens } from 'styling/tokens'
import { OtherText, TitleText } from 'styling/Typography'
import { IFuelDispenserRow } from 'types/fuelCustody.interface'
import { formatDateForDisplay } from 'utils/helpers'
import { RootState } from 'store'
import { CALIBRATION_RESULT_TO_UI } from 'pages/FuelManagement/Shared/fuelEnumMaps'
import { FUEL_DISPENSER_CAPTION } from 'pages/FuelManagement/Shared/fuelFormatters'
import {
    DispenserCellText,
    DispenserKpiStrip,
    DispenserName,
    DispenserNameText,
    DispenserNoteText,
} from './DispensersPanel.styled'

const driftLabel = (driftPct: number | null): string =>
    driftPct == null ? '—' : `${driftPct > 0 ? '+' : ''}${driftPct.toFixed(1)}%`

export const DispensersPanel = () => {
    const dispatch = useDispatch()
    const { dispensers } = useSelector((store: RootState) => store.fuelCustody)

    useEffect(() => {
        dispatch(getFuelDispensers({ page: 1, limit: 50 }) as any)
    }, [dispatch])

    const rows: IFuelDispenserRow[] = useMemo(
        () =>
            dispensers.data.map((d) => ({
                id: d.id,
                name: d.name,
                serves: d.serves || '—',
                lastTest: d.lastTest ? formatDateForDisplay(d.lastTest.checkedAt) : '—',
                driftPct: d.lastTest?.driftPct ?? null,
                result: d.lastTest?.result != null ? CALIBRATION_RESULT_TO_UI[d.lastTest.result].toUpperCase() : '—',
                nextDue: d.nextDueAt ? formatDateForDisplay(d.nextDueAt) : '—',
                statusTone: d.overdue ? 'danger' : 'success',
                statusLabel: d.overdue ? `Overdue ${d.overdueDays}d` : 'Current',
                note: d.note || undefined,
            })),
        [dispensers.data],
    )

    const overdueCount = rows.filter((row) => row.statusTone === 'danger').length
    const worstDrift = rows.reduce(
        (worst: number | null, row) =>
            row.driftPct != null && (worst == null || Math.abs(row.driftPct) > Math.abs(worst))
                ? row.driftPct
                : worst,
        null,
    )

    const columns: IHonestTableColumn<IFuelDispenserRow>[] = [
        {
            id: 'dispenser',
            header: 'Dispenser',
            sticky: true,
            minWidth: '11rem',
            cell: (item) => (
                <DispenserName>
                    <DispenserNameText>{item.name}</DispenserNameText>
                    {item.note && <DispenserNoteText>{item.note}</DispenserNoteText>}
                </DispenserName>
            ),
        },
        {
            id: 'serves',
            header: 'Serves',
            minWidth: '10rem',
            cell: (item) => <DispenserCellText>{item.serves}</DispenserCellText>,
        },
        {
            id: 'lastTest',
            header: 'Last test',
            cell: (item) => (
                <DispenserCellText $tone={item.statusTone === 'danger' ? 'danger' : undefined}>
                    {item.lastTest}
                </DispenserCellText>
            ),
        },
        {
            id: 'drift',
            header: 'Drift',
            align: 'right',
            cell: (item) => (
                <DispenserCellText $tone={item.statusTone === 'warning' ? 'warning' : undefined}>
                    {driftLabel(item.driftPct)}
                </DispenserCellText>
            ),
        },
        {
            id: 'result',
            header: 'Result',
            cell: (item) =>
                item.result === 'PASS' ? (
                    <StatusPill status='success'>PASS</StatusPill>
                ) : (
                    <DispenserCellText>{item.result}</DispenserCellText>
                ),
        },
        {
            id: 'nextDue',
            header: 'Next due',
            cell: (item) => (
                <DispenserCellText $tone={item.statusTone === 'danger' ? 'danger' : undefined}>
                    {item.nextDue}
                </DispenserCellText>
            ),
        },
        {
            id: 'status',
            header: 'Status',
            cell: (item) => <StatusPill status={item.statusTone}>{item.statusLabel}</StatusPill>,
        },
    ]

    return (
        <Container>
            <ContainerHeader>
                <Flex center gap='0.75rem'>
                    <TitleText>Dispenser calibration register</TitleText>
                </Flex>
            </ContainerHeader>
            <ContainerContent gap='1rem'>
                <OtherText grey>{FUEL_DISPENSER_CAPTION}</OtherText>
                <DispenserKpiStrip>
                    <MetricCard
                        value={String(rows.length)}
                        label='DISPENSERS'
                        accentColor={tokens.accent.base}
                    />
                    <MetricCard
                        value={String(overdueCount)}
                        label='OVERDUE'
                        accentColor={overdueCount > 0 ? tokens.status.danger.fg : tokens.status.success.fg}
                    />
                    <MetricCard
                        value={driftLabel(worstDrift)}
                        label='WORST DRIFT'
                        accentColor={tokens.text.primary}
                    />
                </DispenserKpiStrip>
                {!rows.length ? (
                    <NoData text='No dispensers registered yet.' />
                ) : (
                    <HonestTable columns={columns} data={rows} keyField='id' emptyMessage='No dispensers.' />
                )}
            </ContainerContent>
        </Container>
    )
}
